import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "./services/supabase";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swishNumber, setSwishNumber] = useState("");
  const [linkedParticipantId, setLinkedParticipantId] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  // Fetch the user's profile from user_profiles table
  const fetchUserProfile = useCallback(async (userId) => {
    if (!userId) return;
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("swish_number")
        .eq("user_id", userId)
        .maybeSingle();

      if (!error && data) {
        setSwishNumber(data.swish_number || "");
        setIsNewUser(false);
      } else {
        // No profile row exists — this is a first-time sign-in
        setSwishNumber("");
        setIsNewUser(true);
      }

      // Fetch linked participant
      const { data: participant } = await supabase
        .from("participants")
        .select("id")
        .eq("linked_user_id", userId)
        .maybeSingle();

      setLinkedParticipantId(participant?.id || null);
    } catch (err) {
      console.error("Error fetching user profile:", err);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    // Get the initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        fetchUserProfile(currentUser.id);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        setLoading(false);
        if (currentUser) {
          fetchUserProfile(currentUser.id);
        } else {
          // Reset profile data on sign out
          setSwishNumber("");
          setLinkedParticipantId(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchUserProfile]);

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setSwishNumber("");
      setLinkedParticipantId(null);
    }
    return { error };
  };

  // Save Swish number to user_profiles (upsert)
  const updateSwishNumber = async (number) => {
    if (!user) return { error: "Not authenticated" };

    const { error } = await supabase
      .from("user_profiles")
      .upsert(
        {
          user_id: user.id,
          swish_number: number || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );

    if (!error) {
      setSwishNumber(number || "");
      setIsNewUser(false); // Profile saved, no longer new
    }
    return { error };
  };

  // Link a participant to this authenticated user
  const linkParticipant = async (participantId) => {
    if (!user) return { error: "Not authenticated" };

    // First unlink any existing participant for this user
    await supabase
      .from("participants")
      .update({ linked_user_id: null })
      .eq("linked_user_id", user.id);

    // Link the new participant
    const { error } = await supabase
      .from("participants")
      .update({ linked_user_id: user.id })
      .eq("id", participantId);

    if (!error) {
      setLinkedParticipantId(participantId);
    }
    return { error };
  };

  // Unlink the current participant
  const unlinkParticipant = async () => {
    if (!user || !linkedParticipantId) return { error: "Nothing to unlink" };

    const { error } = await supabase
      .from("participants")
      .update({ linked_user_id: null })
      .eq("id", linkedParticipantId);

    if (!error) {
      setLinkedParticipantId(null);
    }
    return { error };
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isNewUser,
    setIsNewUser,
    signInWithGoogle,
    signOut,
    // Profile data
    swishNumber,
    linkedParticipantId,
    profileLoading,
    updateSwishNumber,
    linkParticipant,
    unlinkParticipant,
    refreshUserProfile: () => user && fetchUserProfile(user.id),
    // Helper: get user display info from Google profile
    userProfile: user ? {
      name: user.user_metadata?.full_name || user.email?.split("@")[0] || "User",
      email: user.email || "",
      avatarUrl: user.user_metadata?.avatar_url || null,
    } : null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
